import { useState, useEffect, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { hexToRgb } from "@/lib/color";
import { X, Image as ImageIcon, Download, Quote, Megaphone, Hash, LayoutTemplate, Wand2, Upload, Loader2, RefreshCw, Palette } from "lucide-react";

const PAPER = "#FBFAF8";
const INK = "#1F1F1F";

function rgba(hex, a) { const { r, g, b } = hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; }
function stripEmoji(s) {
  if (!s) return s;
  return s.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}\u{200D}\u{2190}-\u{21FF}]/gu, "").replace(/\s{2,}/g, " ").trim();
}

const templates = [
  { key: "citation", label: "Citation", icon: Quote },
  { key: "annonce", label: "Annonce", icon: Megaphone },
  { key: "chiffre", label: "Chiffre-clé", icon: Hash },
];

const AI_STYLES = [
  { key: "gradient_typo", label: "Dégradé & Typo", thumb: "/styles/gradient_typo.jpeg", desc: "Fond abstrait + votre phrase-clé en grand." },
  { key: "editorial", label: "Illustration éditoriale", thumb: "/styles/editorial.jpeg", desc: "Scène conceptuelle en silhouettes stylisées." },
  { key: "soft3d", label: "3D minimaliste", thumb: "/styles/soft3d.jpeg", desc: "Objets 3D doux, ombres portées." },
  { key: "photo_object", label: "Photo d'objet", thumb: "/styles/photo_object.jpeg", desc: "Nature morte photoréaliste, sans personnes." },
  { key: "isometric", label: "Isométrique", thumb: "/styles/isometric.jpeg", desc: "Réseaux, connexions, parcours." },
  { key: "papercut", label: "Papier découpé", thumb: "/styles/papercut.jpeg", desc: "Collage de papier en relief." },
];

function extractKeyPhrase(text) {
  if (!text) return "";
  const clean = text.replace(/\n+/g, " ").replace(/[\u2014\u2013]/g, ", ").replace(/,\s*,/g, ", ").trim();
  const firstSentence = clean.split(/(?<=[.!?…])\s/)[0] || clean;
  return firstSentence.slice(0, 160).trim();
}

function extractShortPhrase(text) {
  return extractKeyPhrase(text).split(" ").slice(0, 4).join(" ");
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

export default function BrandTemplateStudio({ postContent, authorNameDefault, onClose, onAttach, initialExpertPrompt }) {
  const canvasRef = useRef(null);
  const [kit, setKit] = useState(null);
  const [mode, setMode] = useState("post");
  const [advanced, setAdvanced] = useState(false);
  const [aspect, setAspect] = useState("1:1");
  const [includePerson, setIncludePerson] = useState("auto");
  const [tpl, setTpl] = useState("citation");
  const [variant, setVariant] = useState("color"); // color | light
  const [format, setFormat] = useState("1920x1080");
  const [authorName, setAuthorName] = useState(authorNameDefault || "");
  const [quote, setQuote] = useState(extractKeyPhrase(postContent));
  const [keyPhrase, setKeyPhrase] = useState(extractShortPhrase(postContent));
  const [imgStyle, setImgStyle] = useState("editorial");
  const [expertPrompt, setExpertPrompt] = useState("");
  const [enabledStyles, setEnabledStyles] = useState([]);
  const [title, setTitle] = useState(extractKeyPhrase(postContent).toUpperCase().slice(0, 40));
  const [dateTxt, setDateTxt] = useState("");
  const [showArrow, setShowArrow] = useState(true);
  const [number, setNumber] = useState("100%");
  const [context, setContext] = useState(extractKeyPhrase(postContent));
  const [usePhoto, setUsePhoto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiImages, setAiImages] = useState([]);
  const [bgImage, setBgImage] = useState(null);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [quota, setQuota] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);

  useEffect(() => {
    api.get("/brand-kit").then((r) => {
      setKit(r.data);
      setAuthorName(r.data.name || authorNameDefault || "");
      setUsePhoto(!!r.data.portrait_data);
    });
    api.get("/images/quota").then((r) => {
      setQuota(r.data); setAiEnabled(!!r.data.enabled);
      const es = r.data.enabled_styles || [];
      setEnabledStyles(es);
      setImgStyle((cur) => (es.length && !es.includes(cur) && cur !== "expert" ? es[0] : cur));
    }).catch(() => {});
  }, [authorNameDefault]);

  useEffect(() => {
    if (initialExpertPrompt) {
      setAdvanced(true);
      setMode("illustration");
      setImgStyle("expert");
      setExpertPrompt(initialExpertPrompt);
    }
  }, [initialExpertPrompt]);

  const primary = kit?.primary_color || "#2B2B2B";
  const hasPortrait = !!kit?.portrait_data;

  const pollJob = (jobId) => new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (data.status === "done") return resolve(data);
        if (data.status === "error") return reject(new Error(data.error || "Échec."));
        if (Date.now() - started > 5 * 60 * 1000) return reject(new Error("Délai dépassé."));
        setTimeout(tick, 2500);
      } catch (e) { reject(e); }
    };
    tick();
  });

  const runGenerate = async (styleArg) => {
    const st = styleArg || imgStyle;
    if (st === "expert" && !expertPrompt.trim()) { toast.error("Décrivez votre image (prompt libre)."); return; }
    setAiBusy(true);
    try {
      const payload = { post_content: postContent, instruction: aiInstruction || undefined, style: st };
      if (st === "expert") payload.expert_prompt = expertPrompt.trim();
      if (st === "post") { payload.aspect = aspect; payload.include_person = includePerson; }
      const { data } = await api.post("/images/generate", payload);
      const res = await pollJob(data.job_id);
      setAiImages(res.images || []);
      setAiNote(res.adaptation_note || "");
      if (res.images?.length) setBgImage(res.images[0]);
      api.get("/images/quota").then((r) => setQuota(r.data)).catch(() => {});
      toast.success(`${res.images?.length || 0} visuel(s) généré(s) (${(res.cost_eur || 0).toFixed(2)} €)`);
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally { setAiBusy(false); }
  };
  const generateAI = () => runGenerate();
  const generatePost = () => runGenerate("post");

  const pickUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)."); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const tok = localStorage.getItem("apollo_token");
      setBgImage(`${process.env.REACT_APP_BACKEND_URL}/api/media/${data.id}?token=${tok}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload impossible.");
    }
  };

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !kit) return;
    const [W, H] = format === "1920x1080" ? [1920, 1080] : [1200, 1200];
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");
    try { await document.fonts.ready; } catch (e) {}
    const band = Math.round(H * 0.055);

    // logo
    let logo = null;
    if (kit.logo_data) { try { logo = await loadImage(kit.logo_data); } catch (e) {} }
    const drawSignature = (x, y, targetW, color, nameFallback = true) => {
      if (logo) {
        const ratio = logo.height / logo.width;
        ctx.drawImage(logo, x, y, targetW, targetW * ratio);
      } else if (nameFallback && authorName) {
        ctx.textAlign = "left";
        ctx.fillStyle = color;
        ctx.font = `800 ${Math.round(H * 0.032)}px Poppins, sans-serif`;
        ctx.fillText(authorName, x, y + H * 0.03);
      }
    };

    let photoImg = null;
    if (usePhoto && kit.portrait_data) { try { photoImg = await loadImage(kit.portrait_data); } catch (e) {} }

    // ===== IMAGE DU POST (rendu brut, aucun cadre) =====
    if (mode === "post") {
      ctx.fillStyle = "#0F0F0F"; ctx.fillRect(0, 0, W, H);
      if (bgImage) {
        try { const im = await loadImage(bgImage); const s = Math.min(W / im.width, H / im.height);
          const dw = im.width * s, dh = im.height * s;
          ctx.drawImage(im, (W - dw) / 2, (H - dh) / 2, dw, dh); } catch (e) {}
      } else {
        ctx.fillStyle = "#666"; ctx.font = `500 ${Math.round(H * 0.03)}px Nunito Sans, sans-serif`; ctx.textAlign = "center";
        ctx.fillText("Cliquez sur « Générer l'image du post »", W / 2, H / 2);
      }
      return;
    }

    // ===== ILLUSTRATION / UPLOAD (image de fond) =====
    if (mode !== "gabarit") {
      ctx.fillStyle = "#111"; ctx.fillRect(0, 0, W, H);
      if (bgImage) {
        try { const im = await loadImage(bgImage); const s = Math.max(W / im.width, H / im.height);
          ctx.drawImage(im, (W - im.width * s) / 2, (H - im.height * s) / 2, im.width * s, im.height * s); } catch (e) {}
      } else {
        ctx.fillStyle = "#555"; ctx.font = `500 ${Math.round(H * 0.03)}px Nunito Sans, sans-serif`; ctx.textAlign = "center";
        ctx.fillText(mode === "illustration" ? "Choisissez un style, puis générez" : "Importez une image", W / 2, H / 2);
      }
      const typo = mode === "illustration" && imgStyle === "gradient_typo";
      if (typo) {
        // Dégradé & Typo : phrase-clé en grande typo (encre foncée + halo blanc pour lisibilité sur fond clair)
        const left = W * 0.07;
        const kfs = Math.round(H * 0.11);
        const ink = "#241f1c";
        ctx.textAlign = "left";
        ctx.font = `800 ${kfs}px Poppins, sans-serif`;
        ctx.lineJoin = "round"; ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.lineWidth = Math.round(kfs * 0.16);
        const klines = wrapText(ctx, (keyPhrase || quote || "").toUpperCase(), W * 0.64);
        let ky = H * 0.40;
        for (const ln of klines.slice(0, 4)) { ctx.strokeText(ln, left, ky); ctx.fillStyle = ink; ctx.fillText(ln, left, ky); ky += kfs * 1.12; }
        if (authorName && !logo) {
          const sfs = Math.round(H * 0.035);
          ctx.font = `700 ${sfs}px Poppins, sans-serif`;
          ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.lineWidth = Math.round(sfs * 0.18);
          ctx.strokeText(authorName, left, ky + H * 0.02); ctx.fillStyle = ink; ctx.fillText(authorName, left, ky + H * 0.02);
        }
        drawSignature(left, H - band - H * 0.14, W * 0.15, ink, false);
        ctx.fillStyle = primary; ctx.fillRect(0, 0, W, band); ctx.fillRect(0, H - band, W, band);
      } else {
        // Cadre de marque léger (illustrations éditoriale/3D/photo/iso/papier + upload)
        const gr = ctx.createLinearGradient(0, H * 0.6, 0, H);
        gr.addColorStop(0, "rgba(0,0,0,0)"); gr.addColorStop(1, "rgba(0,0,0,0.65)");
        ctx.fillStyle = gr; ctx.fillRect(0, H * 0.6, W, H * 0.4);
        if (authorName) {
          ctx.textAlign = "left"; ctx.fillStyle = "#fff"; ctx.font = `800 ${Math.round(H * 0.045)}px Poppins, sans-serif`;
          ctx.shadowColor = "rgba(0,0,0,0.5)"; ctx.shadowBlur = 8;
          ctx.fillText(authorName, W * 0.05, band + H * 0.09); ctx.shadowBlur = 0;
        }
        drawSignature(W * 0.05, H - band - H * 0.13, W * 0.16, "#fff", false);
        ctx.fillStyle = primary; ctx.fillRect(0, 0, W, band); ctx.fillRect(0, H - band, W, band);
      }
      return;
    }

    // ===== GABARITS (clone des références : noir profond + orange saturé) =====
    const topBar = Math.round(H * 0.075);
    const botBar = Math.round(H * 0.048);
    const isColor = variant === "color";
    const fg = isColor ? "#FFFFFF" : INK;
    const sub = isColor ? "rgba(255,255,255,0.85)" : "rgba(31,31,31,0.72)";
    const bgStop = (a) => isColor ? `rgba(10,10,10,${a})` : `rgba(251,250,248,${a})`;

    // Fond : noir profond uni (couleur) / ivoire propre (clair)
    ctx.fillStyle = isColor ? "#0A0A0A" : PAPER;
    ctx.fillRect(0, 0, W, H);
    // Bandeaux de marque épais, pleine saturation
    ctx.fillStyle = primary; ctx.fillRect(0, 0, W, topBar); ctx.fillRect(0, H - botBar, W, botBar);

    // Photo N&B pleine hauteur, fondue vers le fond sur son bord intérieur
    const drawPhotoEdge = (side, w) => {
      if (!photoImg) return;
      const x = side === "left" ? 0 : W - w;
      const y = topBar, ph = H - topBar - botBar;
      const sc = Math.max(w / photoImg.width, ph / photoImg.height);
      const dw = photoImg.width * sc, dh = photoImg.height * sc;
      ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, ph); ctx.clip();
      ctx.filter = "grayscale(100%) contrast(1.05)";
      ctx.drawImage(photoImg, x + (w - dw) / 2, y + (ph - dh) / 2, dw, dh);
      ctx.filter = "none";
      const vw = w * 0.55;
      let grad;
      if (side === "left") { grad = ctx.createLinearGradient(x + w - vw, 0, x + w, 0); grad.addColorStop(0, bgStop(0)); grad.addColorStop(0.7, bgStop(0.75)); grad.addColorStop(1, bgStop(1)); }
      else { grad = ctx.createLinearGradient(x, 0, x + vw, 0); grad.addColorStop(0, bgStop(1)); grad.addColorStop(0.3, bgStop(0.75)); grad.addColorStop(1, bgStop(0)); }
      ctx.fillStyle = grad; ctx.fillRect(x, y, w, ph);
      ctx.restore();
    };

    const placeLogo = (x, yTop, targetW, align, vcenterY) => {
      if (logo) {
        const ratio = logo.height / logo.width;
        const lw = targetW, lh = targetW * ratio;
        const ly = vcenterY != null ? vcenterY - lh / 2 : yTop;
        ctx.drawImage(logo, align === "center" ? x - lw / 2 : x, ly, lw, lh);
        return lh;
      }
      if (authorName) {
        const s = Math.round(H * 0.045);
        ctx.textAlign = align === "center" ? "center" : "left";
        ctx.fillStyle = primary; ctx.font = `800 ${s}px Poppins, sans-serif`;
        ctx.fillText(stripEmoji(authorName), x, vcenterY != null ? vcenterY : yTop);
        return s * 1.3;
      }
      return 0;
    };

    if (tpl === "citation") {
      const hasP = !!photoImg;
      if (hasP) drawPhotoEdge("left", Math.round(W * 0.42));
      const cx = hasP ? W * 0.64 : W * 0.5;
      const halfW = hasP ? W * 0.26 : W * 0.42;

      // Guillemet géant orange en haut à droite (comme la référence)
      const qmFS = Math.round(H * 0.28);
      ctx.textAlign = "right";
      ctx.fillStyle = rgba(primary, isColor ? 0.92 : 0.85);
      ctx.font = `900 ${qmFS}px Poppins, sans-serif`;
      ctx.fillText("\u201D", W * 0.95, topBar + qmFS * 0.92);

      // Nom d'auteur en orange gras, grand
      const authorFS = Math.round(H * 0.064);
      ctx.textAlign = "center";
      ctx.fillStyle = primary; ctx.font = `800 ${authorFS}px Poppins, sans-serif`;
      ctx.fillText(stripEmoji(authorName) || "", cx, H * 0.24);

      // Citation blanche italique, grand corps, interlignage serré
      const q = stripEmoji(quote || "");
      const full = q ? (/^[«"“]/.test(q) ? q : `« ${q} »`) : "";
      const len = full.length;
      const qfs = len > 240 ? Math.round(H * 0.04) : len > 150 ? Math.round(H * 0.047) : Math.round(H * 0.054);
      ctx.font = `italic 600 ${qfs}px "Nunito Sans", sans-serif`;
      const qlines = wrapText(ctx, full, halfW * 2);
      const lineH = qfs * 1.3;
      const blockTop = H * 0.31, blockBot = H * 0.55;
      let y = blockTop + ((blockBot - blockTop) - qlines.length * lineH) / 2 + qfs;
      const kp = (stripEmoji(keyPhrase) || "").trim().split(" ")[0];
      for (const ln of qlines) {
        const bold = kp && kp.length > 2 && ln.includes(kp);
        ctx.font = `italic ${bold ? 800 : 600} ${qfs}px "Nunito Sans", sans-serif`;
        ctx.fillStyle = "#FFFFFF"; if (!isColor) ctx.fillStyle = INK;
        ctx.fillText(ln, cx, y); y += lineH;
      }
      // Logo grand, centré (position référence)
      placeLogo(cx, 0, W * 0.24, "center", H * 0.73);
    }

    if (tpl === "annonce") {
      const hasP = !!photoImg;
      if (hasP) drawPhotoEdge("right", Math.round(W * 0.48));
      const left = W * 0.06;
      const blockW = hasP ? W * 0.46 : W * 0.86;
      ctx.textAlign = "left";

      const an = stripEmoji(authorName || "").toUpperCase();
      let y = H * 0.17;
      if (an) {
        const afs = Math.round(H * 0.05);
        let x = left;
        an.split(" ").forEach((w, i) => {
          ctx.font = `${i === 0 ? 800 : 400} ${afs}px Poppins, sans-serif`;
          ctx.fillStyle = fg; ctx.fillText(w, x, y);
          x += ctx.measureText(w).width + afs * 0.3;
        });
        y += afs * 0.5;
      }
      const t = stripEmoji(title || "").toUpperCase();
      const tfs = t.length > 26 ? Math.round(H * 0.095) : Math.round(H * 0.14);
      ctx.font = `800 ${tfs}px Poppins, sans-serif`;
      const tlines = wrapText(ctx, t, blockW - (showArrow ? tfs * 1.1 : 0));
      y += tfs;
      tlines.forEach((ln, i) => {
        ctx.fillStyle = fg; ctx.font = `800 ${tfs}px Poppins, sans-serif`;
        ctx.fillText(ln, left, y);
        if (i === 0 && showArrow) {
          const ax = left + ctx.measureText(ln).width + tfs * 0.3, ay = y - tfs * 0.78, s = tfs * 0.7;
          ctx.strokeStyle = fg; ctx.lineWidth = Math.max(7, H * 0.011);
          ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.beginPath();
          ctx.moveTo(ax, ay + s); ctx.lineTo(ax + s, ay);
          ctx.moveTo(ax + s * 0.42, ay); ctx.lineTo(ax + s, ay); ctx.lineTo(ax + s, ay + s * 0.58); ctx.stroke();
        }
        y += tfs * 1.02;
      });
      const d = stripEmoji(dateTxt || "");
      if (d) {
        const dfs = Math.round(H * 0.05);
        ctx.font = `400 ${dfs}px "Nunito Sans", sans-serif`; ctx.fillStyle = sub;
        y += dfs * 0.4; ctx.fillText(d, left, y + dfs);
      }
      placeLogo(left, H - botBar - H * 0.19, W * 0.19, "left");
    }

    if (tpl === "chiffre") {
      const hasP = !!photoImg;
      if (hasP) drawPhotoEdge("right", Math.round(W * 0.42));
      const left = W * 0.07;
      const blockW = hasP ? W * 0.5 : W * 0.82;
      ctx.textAlign = "left";
      const nfs = Math.round(H * 0.34);
      ctx.font = `800 ${nfs}px Poppins, sans-serif`; ctx.fillStyle = primary;
      const numY = H * 0.44;
      ctx.fillText(stripEmoji(number || ""), left, numY);
      const cfs = Math.round(H * 0.056);
      ctx.font = `600 ${cfs}px "Nunito Sans", sans-serif`;
      const clines = wrapText(ctx, stripEmoji(context || ""), blockW);
      let cy = numY + H * 0.08;
      clines.forEach((ln) => { ctx.fillStyle = fg; ctx.fillText(ln, left, cy); cy += cfs * 1.32; });
      placeLogo(left, H - botBar - H * 0.22, W * 0.2, "left");
    }
  }, [kit, mode, tpl, variant, format, authorName, quote, keyPhrase, title, dateTxt, showArrow, number, context, usePhoto, bgImage, primary, imgStyle]);

  useEffect(() => { draw(); }, [draw]);

  const attach = async () => {
    if (mode === "post") {
      if (!bgImage) { toast.error("Générez d'abord l'image du post."); return; }
      onAttach(bgImage, { image_mode: "post", image_prompt: aiInstruction || undefined });
      return;
    }
    setBusy(true);
    try {
      await draw();
      const meta = { image_mode: mode === "illustration" ? imgStyle : mode,
                     image_prompt: (mode === "illustration" && imgStyle === "expert") ? expertPrompt.trim() : undefined };
      onAttach(canvasRef.current.toDataURL("image/png"), meta);
    } finally { setBusy(false); }
  };

  const download = () => {
    if (mode === "post" && bgImage) {
      const a = document.createElement("a"); a.href = bgImage; a.download = "pulse-image-post.png"; a.click(); return;
    }
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = `pulse-visuel-${mode}-${format}.png`; a.click();
  };

  const inputCls = "w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors";
  const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-ink-light/70";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-dark/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div data-testid="brand-studio" className="bg-white border border-border rounded-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2 text-ink">
            <ImageIcon className="h-5 w-5 text-coral" />
            <span className="font-display font-bold text-xl">Studio visuel</span>
          </div>
          <button data-testid="close-brand-studio" onClick={onClose} className="text-ink-light hover:text-ink transition-colors"><X className="h-5 w-5" /></button>
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-0 flex-1 overflow-hidden">
          <div className="p-6 bg-secondary flex flex-col items-center justify-center overflow-auto">
            <canvas ref={canvasRef} data-testid="brand-canvas" className="w-full max-w-full border border-border rounded-lg shadow-sm" style={{ aspectRatio: mode === "post" ? (aspect === "1:1" ? "1/1" : aspect === "4:5" ? "4/5" : "16/9") : (format === "1920x1080" ? "16/9" : "1/1"), background: "#000" }} />
            <p className="text-xs text-ink-light/60 mt-3">Aperçu : export {format.replace("x", " × ")} px</p>
          </div>

          <div className="p-6 border-l border-border overflow-y-auto space-y-5">
            {!advanced && (
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Image du post</label>
                  <p className="text-xs text-ink-light mt-1">Un clic : l'IA crée un visuel LinkedIn complet à partir de votre post (titre, encarts, icônes si utile). Texte en français.</p>
                </div>
                {quota && <p className="text-xs text-ink-light">Images ce mois-ci : <span className="font-bold text-ink">{quota.used}/{quota.limit}</span> · {quota.remaining} restantes</p>}
                {kit && !kit.configured && (
                  <a href="/marque" data-testid="brandkit-incitation-post"
                    className="flex items-start gap-2 text-[11px] bg-coral-soft text-coral-dark rounded-lg px-3 py-2 hover:bg-coral-soft/70 transition-colors">
                    <Palette className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><span className="font-bold">Configurez votre marque</span> pour suggérer votre palette (facultatif).</span>
                  </a>
                )}
                <div>
                  <label className={labelCls}>Format</label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    <button data-testid="post-aspect-11" onClick={() => setAspect("1:1")}
                      className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${aspect === "1:1" ? "bg-coral-soft border-coral text-coral-dark" : "border-border text-ink-light hover:border-coral/50"}`}>Carré 1:1</button>
                    <button data-testid="post-aspect-45" onClick={() => setAspect("4:5")}
                      className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${aspect === "4:5" ? "bg-coral-soft border-coral text-coral-dark" : "border-border text-ink-light hover:border-coral/50"}`}>Vertical 4:5</button>
                    <button data-testid="post-aspect-169" onClick={() => setAspect("16:9")}
                      className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${aspect === "16:9" ? "bg-coral-soft border-coral text-coral-dark" : "border-border text-ink-light hover:border-coral/50"}`}>Paysage 16:9</button>
                  </div>
                  <p className="text-[11px] text-ink-light/60 mt-1">Le carré et le vertical occupent plus de place dans le fil mobile.</p>
                </div>
                <div>
                  <label className={labelCls}>M'inclure dans le visuel</label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {[["auto", "Auto"], ["oui", "Oui"], ["non", "Non"]].map(([v, lbl]) => (
                      <button key={v} data-testid={`post-include-${v}`} onClick={() => setIncludePerson(v)}
                        className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${includePerson === v ? "bg-coral-soft border-coral text-coral-dark" : "border-border text-ink-light hover:border-coral/50"}`}>{lbl}</button>
                    ))}
                  </div>
                  <p className="text-[11px] text-ink-light/60 mt-1">« Auto » décide selon le post. Utilise vos photos de « Ma marque ».</p>
                </div>
                <div>
                  <label className={labelCls}>Instruction (optionnel)</label>
                  <textarea data-testid="post-instruction" rows={2} value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)} className={`${inputCls} mt-1.5 resize-none`} placeholder="Ex : mets l'accent sur les 4 questions, style plus sobre…" />
                </div>
                <button data-testid="generate-post-image-btn" onClick={generatePost} disabled={aiBusy || (quota && quota.remaining <= 0)}
                  className="w-full flex items-center justify-center gap-2 bg-coral-strong text-white py-3 rounded-lg text-sm font-bold hover:bg-coral-hover transition-colors disabled:opacity-60">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {aiBusy ? "Génération…" : aiImages.length ? "Générer à nouveau" : "Générer l'image du post"}
                </button>
                {aiBusy && <p className="text-[11px] text-ink-light/60 text-center">La génération peut prendre 30 à 90 secondes…</p>}
                {aiImages.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {aiImages.map((img, i) => (
                      <button key={i} data-testid={`post-image-${i}`} onClick={() => setBgImage(img)}
                        className={`rounded-lg overflow-hidden border-2 transition-colors ${bgImage === img ? "border-coral" : "border-transparent hover:border-border"}`}>
                        <img src={img} alt={`visuel ${i + 1}`} className="w-full h-24 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
                <button data-testid="advanced-toggle" onClick={() => { setAdvanced(true); setMode("gabarit"); }}
                  className="text-xs font-semibold text-ink-light hover:text-coral underline underline-offset-2 transition-colors">
                  Personnaliser (avancé) : gabarits, styles, upload
                </button>
              </div>
            )}

            {advanced && (<>
            <button data-testid="back-to-post" onClick={() => { setAdvanced(false); setMode("post"); }}
              className="text-xs font-semibold text-ink-light hover:text-coral underline underline-offset-2 transition-colors">← Revenir à l'image du post</button>
            <div>
              <label className={labelCls}>Mode</label>
              <div className={`grid gap-2 mt-2 ${aiEnabled ? "grid-cols-3" : "grid-cols-2"}`}>
                {[{ key: "gabarit", label: "Gabarit", icon: LayoutTemplate }, ...(aiEnabled ? [{ key: "illustration", label: "Illustration IA", icon: Wand2 }] : []), { key: "upload", label: "Upload", icon: Upload }].map((m) => (
                  <button key={m.key} data-testid={`mode-${m.key}`} onClick={() => setMode(m.key)}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-[11px] font-semibold transition-colors ${mode === m.key ? "border-coral bg-coral-soft text-coral-dark" : "border-border text-ink-light hover:border-coral"}`}>
                    <m.icon className="h-4 w-4" /> {m.label}
                  </button>
                ))}
              </div>
            </div>

            {mode === "gabarit" && (
              <>
                <div>
                  <label className={labelCls}>Gabarit</label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {templates.map((t) => (
                      <button key={t.key} data-testid={`tpl-${t.key}`} onClick={() => setTpl(t.key)}
                        className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-[11px] font-semibold transition-colors ${tpl === t.key ? "border-coral bg-coral-soft text-coral-dark" : "border-border text-ink-light hover:border-coral"}`}>
                        <t.icon className="h-4 w-4" /> {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Variante</label>
                  <div className="flex gap-2 mt-2">
                    {[["color", "Fond couleur"], ["light", "Fond clair"]].map(([v, lbl]) => (
                      <button key={v} data-testid={`variant-${v}`} onClick={() => setVariant(v)}
                        className={`flex-1 py-2 rounded-lg border text-xs font-semibold transition-colors ${variant === v ? "border-coral bg-coral-soft text-coral-dark" : "border-border text-ink-light hover:border-coral"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {mode === "illustration" && (
              <div className="space-y-3">
                {quota && <p className="text-xs text-ink-light">Illustrations ce mois-ci : <span className="font-bold text-ink">{quota.used}/{quota.limit}</span> · {quota.remaining} restantes</p>}
                {kit && !kit.configured && (
                  <a href="/marque" data-testid="brandkit-incitation"
                    className="flex items-start gap-2 text-[11px] bg-coral-soft text-coral-dark rounded-lg px-3 py-2 hover:bg-coral-soft/70 transition-colors">
                    <Palette className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><span className="font-bold">Configurez votre marque</span> pour des visuels à vos couleurs. Sans cela, la palette par défaut est utilisée.</span>
                  </a>
                )}
                <div>
                  <label className={labelCls}>Style visuel</label>
                  <div className="grid grid-cols-3 gap-2 mt-2" data-testid="style-gallery">
                    {AI_STYLES.filter((s) => enabledStyles.includes(s.key)).map((s) => (
                      <button key={s.key} data-testid={`style-${s.key}`} onClick={() => setImgStyle(s.key)} title={s.desc}
                        className={`group rounded-lg overflow-hidden border-2 transition-colors ${imgStyle === s.key ? "border-coral" : "border-transparent hover:border-border"}`}>
                        <img src={s.thumb} alt={s.label} className="w-full h-14 object-cover" />
                        <span className={`block text-[10px] font-semibold py-1 px-1 leading-tight ${imgStyle === s.key ? "bg-coral-soft text-coral-dark" : "bg-secondary text-ink-light"}`}>{s.label}</span>
                      </button>
                    ))}
                    <button data-testid="style-expert" onClick={() => setImgStyle("expert")} title="Écrivez votre propre description d'image"
                      className={`rounded-lg overflow-hidden border-2 transition-colors ${imgStyle === "expert" ? "border-coral" : "border-transparent hover:border-border"}`}>
                      <div className="w-full h-14 flex items-center justify-center bg-ink/90"><Wand2 className="h-5 w-5 text-white" /></div>
                      <span className={`block text-[10px] font-semibold py-1 px-1 leading-tight ${imgStyle === "expert" ? "bg-coral-soft text-coral-dark" : "bg-secondary text-ink-light"}`}>Prompt libre (expert)</span>
                    </button>
                  </div>
                  <p className="text-[11px] text-ink-light/60 mt-1.5">{imgStyle === "expert" ? "Votre description est envoyée telle quelle au modèle (qualité max). Le cadre de marque et le garde-fou anti-texte restent appliqués." : AI_STYLES.find((s) => s.key === imgStyle)?.desc}</p>
                </div>
                {imgStyle === "expert" && (
                  <div>
                    <label className={labelCls}>Votre prompt (description de l'image)</label>
                    <textarea data-testid="ai-expert-prompt" rows={3} value={expertPrompt} onChange={(e) => setExpertPrompt(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="Ex. A minimalist desk seen from above with a single coral notebook, soft daylight, lots of negative space" />
                  </div>
                )}
                {imgStyle === "gradient_typo" && (
                  <div>
                    <label className={labelCls}>Phrase-clé (compositée en grand)</label>
                    <input data-testid="ai-keyphrase" value={keyPhrase} onChange={(e) => setKeyPhrase(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="Votre accroche courte" />
                  </div>
                )}
                <button data-testid="ai-generate-btn" onClick={generateAI} disabled={aiBusy || (quota && quota.remaining <= 0)}
                  className="w-full flex items-center justify-center gap-2 bg-coral-strong text-white py-2.5 rounded-lg text-sm font-bold hover:bg-coral-hover transition-colors disabled:opacity-60">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {aiBusy ? "Génération…" : aiImages.length ? "Générer à nouveau" : "Générer 2 illustrations"}
                </button>
                {aiBusy && <p className="text-[11px] text-ink-light/60 text-center">La génération peut prendre 30 à 90 secondes…</p>}
                {aiImages.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {aiImages.map((img, i) => (
                      <button key={i} data-testid={`ai-image-${i}`} onClick={() => setBgImage(img)}
                        className={`rounded-lg overflow-hidden border-2 transition-colors ${bgImage === img ? "border-coral" : "border-transparent hover:border-border"}`}>
                        <img src={img} alt={`illustration ${i + 1}`} className="w-full h-24 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
                {aiNote && (
                  <p data-testid="ai-adaptation-note" className="text-[11px] text-coral-dark bg-coral-soft rounded-lg px-3 py-2">{aiNote}</p>
                )}
                <div>
                  <label className={labelCls}>Régénérer avec une instruction</label>
                  <div className="flex gap-2 mt-1.5">
                    <input data-testid="ai-instruction" value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)} className={inputCls} placeholder="Ex : plus abstrait, tons chauds…" />
                    <button data-testid="ai-regenerate-btn" onClick={generateAI} disabled={aiBusy} className="px-3 border border-border rounded-lg text-ink-light hover:border-coral hover:text-coral transition-colors disabled:opacity-60"><RefreshCw className="h-4 w-4" /></button>
                  </div>
                </div>
                <p className="text-[11px] text-ink-light/60">L'IA n'ajoute jamais de texte ni de logo. Le cadre de votre marque est composité par-dessus.</p>
              </div>
            )}

            {mode === "upload" && (
              <div>
                <label className={labelCls}>Importer une image</label>
                <label data-testid="upload-bg-label" className="mt-2 flex items-center justify-center gap-2 border border-dashed border-border rounded-lg py-6 text-sm text-ink-light cursor-pointer hover:border-coral hover:text-coral transition-colors">
                  <Upload className="h-4 w-4" /> Choisir une image
                  <input data-testid="upload-bg-input" type="file" accept="image/*" onChange={pickUpload} className="hidden" />
                </label>
              </div>
            )}

            <div>
              <label className={labelCls}>Format</label>
              <div className="flex gap-2 mt-2">
                {["1920x1080", "1200x1200"].map((f) => (
                  <button key={f} data-testid={`fmt-${f}`} onClick={() => setFormat(f)}
                    className={`flex-1 py-2 rounded-lg border text-xs font-semibold transition-colors ${format === f ? "border-coral bg-coral-soft text-coral-dark" : "border-border text-ink-light hover:border-coral"}`}>{f === "1920x1080" ? "Paysage 16:9" : "Carré 1:1"}</button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>Signature : nom</label>
              <input data-testid="studio-author" value={authorName} onChange={(e) => setAuthorName(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="Votre nom" />
            </div>

            {mode === "gabarit" && hasPortrait && (
              <label className="flex items-center gap-2 text-sm text-ink-light">
                <input data-testid="studio-usephoto" type="checkbox" checked={usePhoto} onChange={(e) => setUsePhoto(e.target.checked)} /> Utiliser ma photo
              </label>
            )}
            {mode === "gabarit" && !hasPortrait && <p className="text-xs text-ink-light/60">Ajoutez une photo dans « Ma marque » pour l'intégrer.</p>}

            {mode === "gabarit" && tpl === "citation" && (
              <>
                <div><label className={labelCls}>Citation</label><textarea data-testid="studio-quote" rows={3} value={quote} onChange={(e) => setQuote(e.target.value)} className={`${inputCls} mt-1.5 resize-none`} /></div>
                <div><label className={labelCls}>Phrase-clé (en gras)</label><input data-testid="studio-keyphrase" value={keyPhrase} onChange={(e) => setKeyPhrase(e.target.value)} className={`${inputCls} mt-1.5`} /></div>
              </>
            )}
            {mode === "gabarit" && tpl === "annonce" && (
              <>
                <div><label className={labelCls}>Titre (capitales)</label><input data-testid="studio-title" value={title} onChange={(e) => setTitle(e.target.value)} className={`${inputCls} mt-1.5`} /></div>
                <div><label className={labelCls}>Date (optionnel)</label><input data-testid="studio-date" value={dateTxt} onChange={(e) => setDateTxt(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="Jeudi 28 nov." /></div>
                <label className="flex items-center gap-2 text-sm text-ink-light"><input type="checkbox" checked={showArrow} onChange={(e) => setShowArrow(e.target.checked)} /> Flèche décorative</label>
              </>
            )}
            {mode === "gabarit" && tpl === "chiffre" && (
              <>
                <div><label className={labelCls}>Chiffre-clé</label><input data-testid="studio-number" value={number} onChange={(e) => setNumber(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="+300 %" /></div>
                <div><label className={labelCls}>Contexte</label><textarea data-testid="studio-context" rows={2} value={context} onChange={(e) => setContext(e.target.value)} className={`${inputCls} mt-1.5 resize-none`} /></div>
              </>
            )}
            </>)}
          </div>
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-border">
          <button data-testid="attach-visual-btn" onClick={attach} disabled={busy || (mode !== "gabarit" && !bgImage)}
            className="flex-1 bg-coral-strong text-white py-2.5 rounded-lg text-sm font-bold hover:bg-coral-hover transition-colors disabled:opacity-60">
            {busy ? "…" : "Attacher au post"}
          </button>
          <button data-testid="download-visual-btn" onClick={download}
            className="flex items-center gap-2 border border-border text-ink-light py-2.5 px-4 rounded-lg text-sm font-semibold hover:border-coral hover:text-coral transition-colors">
            <Download className="h-4 w-4" /> Télécharger
          </button>
        </div>
      </div>
    </div>
  );
}
